import { createSelector } from '@reduxjs/toolkit';
import type { AssetListItem } from '@fleet-bffs/api-contract';
import { ShortGenericNameEnum } from 'fleet-data-model';
import assetsAPI from '~/api/assets-api/assets-api';
import { isRoboticMower } from '~/data/AssetListItem/selectors';
import type { AppState } from '~/reducers';
import { selectLoadedSelectedRows } from './loaded-rows-selectors';

const NO_IDS: string[] = [];

/** The paged list's batch selection, with select all resolved to asset ids. */
export interface InventoryV2ResolvedSelection {
  /** Whether any row is selected. */
  active: boolean;
  /** Select all is on and its ids have not arrived yet, or a filter change is checking which still match. */
  isResolving: boolean;
  /** Select all could not resolve its ids. */
  isError: boolean;
  /** Selected assets; while select all resolves, the matching total minus the unpicked rows. */
  count: number;
  ids: string[];
  roboticMowerIds: string[];
  proRoboticMowerIds: string[];
}

const selectSelection = (state: AppState) => state.inventoryV2Selection;

/** The select-all range request for the current selection. */
export const selectInventoryV2RangeArgs = createSelector(
  [
    (state: AppState) => state.inventoryV2Selection.filters,
    (state: AppState) => state.inventoryV2Selection.requestedAt,
  ],
  (filters, requestedAt) => ({ filters: filters as Record<string, string>, requestedAt })
);

const selectRangeSelector = createSelector([selectInventoryV2RangeArgs], (args) =>
  assetsAPI.endpoints.getPagedListSelectionRange.select(args)
);

const selectRangeEntry = (state: AppState) =>
  state.inventoryV2Selection.selectAll ? selectRangeSelector(state)(state as never) : undefined;

export const selectInventoryV2Selection = createSelector(
  [selectSelection, selectRangeEntry, selectLoadedSelectedRows],
  (selection, entry, loadedRows): InventoryV2ResolvedSelection => {
    if (!selection.selectAll) {
      const ids = [
        ...selection.toggled.map((asset) => asset.assetDetails.id),
        ...selection.seeds.map((seed) => seed.iprId),
      ];
      const seedRows = selection.seeds.map((seed) => loadedRows.get(seed.iprId));
      const roboticMowers = [
        ...selection.toggled,
        ...seedRows.filter((row): row is AssetListItem => row !== undefined),
      ].filter(isRoboticMower);
      const isPicking = !!selection.pickToken;
      return {
        active: ids.length > 0 || isPicking,
        isResolving: isPicking,
        isError: false,
        count: ids.length,
        ids,
        roboticMowerIds: [
          ...roboticMowers.map((asset) => asset.assetDetails.id),
          ...selection.seeds
            .filter(
              (seed, index) =>
                !seedRows[index] &&
                seed.shortGenericName === ShortGenericNameEnum.ROBOTIC_LAWN_MOWER
            )
            .map((seed) => seed.iprId),
        ],
        proRoboticMowerIds: roboticMowers
          .filter((asset) => asset.roboticMower?.intendedForPro)
          .map((asset) => asset.assetDetails.id),
      };
    }

    const unpicked = new Set([
      ...selection.toggled.map((asset) => asset.assetDetails.id),
      ...selection.seeds.map((seed) => seed.iprId),
    ]);
    const resolved = entry?.isSuccess ? entry.data.assets : undefined;
    if (resolved === undefined || (resolved.length === 0 && selection.total > 0)) {
      const isError = !!entry?.isError || resolved !== undefined;
      return {
        active: true,
        isResolving: !isError,
        isError,
        count: Math.max(0, selection.total - unpicked.size),
        ids: NO_IDS,
        roboticMowerIds: NO_IDS,
        proRoboticMowerIds: NO_IDS,
      };
    }
    const matching = resolved.filter((asset) => !unpicked.has(asset.iprId));
    const roboticMowers = matching.filter(
      (asset) => asset.shortGenericNameEnum === ShortGenericNameEnum.ROBOTIC_LAWN_MOWER
    );
    return {
      active: true,
      isResolving: false,
      isError: false,
      count: matching.length,
      ids: matching.map((asset) => asset.iprId),
      roboticMowerIds: roboticMowers.map((asset) => asset.iprId),
      proRoboticMowerIds: roboticMowers
        .filter((asset) => asset.intendedForPro)
        .map((asset) => asset.iprId),
    };
  }
);

/**
 * Assets to mark as selected on the inventory map: the paged list's selection plus the ids the
 * whole-fleet list keeps selected (its own selection, or a dashboard map cluster).
 */
export const selectMapSelectedAssetIds = createSelector(
  [(state: AppState) => state.assetList.selectedAssetIds, selectInventoryV2Selection],
  (wholeFleetIds, paged) => {
    if (paged.ids.length === 0) {
      return wholeFleetIds;
    }
    if (wholeFleetIds.length === 0) {
      return paged.ids;
    }
    return Array.from(new Set([...wholeFleetIds, ...paged.ids]));
  }
);
